import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Button,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { api } from '../../services/authService';
import RetryStrategyForm from './RetryStrategyForm';

const RetryStrategySelector = ({
  value,
  onChange,
  showDefault = true,
  allowCreate = true,
  disabled = false,
  showPreview = true,
  label = "Retry Strategy"
}) => {
  const [strategies, setStrategies] = useState([]);
  const [defaultStrategy, setDefaultStrategy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);

  // Fetch strategies on component mount
  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    setLoading(true);
    setError(null);
    try {
      const [strategiesResponse, defaultResponse] = await Promise.all([
        api.get('/acs/retry-strategies/'),
        api.get('/acs/retry-strategies/default/')
      ]);
      setStrategies(strategiesResponse.data);
      setDefaultStrategy(defaultResponse.data);
    } catch (err) {
      setError('Failed to load retry strategies');
      console.error('Error fetching retry strategies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = () => {
    setEditingStrategy(null);
    setCreateDialogOpen(true);
  };

  const handleEditStrategy = (strategy) => {
    setEditingStrategy(strategy);
    setEditDialogOpen(true);
  };

  const handleSaveStrategy = async (strategyData) => {
    try {
      if (editingStrategy) {
        await api.put(`/acs/retry-strategies/${editingStrategy.id}/`, strategyData);
      } else {
        await api.post('/acs/retry-strategies/', strategyData);
      }
      
      // Refresh strategies
      await fetchStrategies();
      
      // Close dialog
      setCreateDialogOpen(false);
      setEditDialogOpen(false);
      setEditingStrategy(null);
      
      // If this was a new strategy, select it
      if (!editingStrategy) {
        // Find the newly created strategy and select it
        const newStrategy = strategies.find(s => s.name === strategyData.name);
        if (newStrategy) {
          onChange(newStrategy.id);
        }
      }
    } catch (err) {
      setError('Failed to save retry strategy');
      console.error('Error saving retry strategy:', err);
    }
  };

  const getSelectedStrategy = () => {
    if (value === 'default') return defaultStrategy;
    return strategies.find(s => s.id === value) || null;
  };

  const renderStrategyPreview = (strategy) => {
    if (!strategy || !showPreview) return null;

    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Max Attempts:</strong> {strategy.max_attempts} | 
          <strong>Base Delay:</strong> {strategy.base_delay_minutes}min | 
          <strong>Backoff:</strong> {strategy.backoff_factor}x
        </Typography>
        {strategy.description && (
          <Typography variant="caption" display="block" color="text.secondary">
            {strategy.description}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStrategyChip = (strategy) => {
    if (!strategy) return null;

    const isDefault = strategy.id === defaultStrategy?.id;
    return (
      <Chip
        label={strategy.name}
        size="small"
        color={isDefault ? "default" : "primary"}
        icon={isDefault ? <InfoIcon /> : undefined}
        sx={{ ml: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading strategies...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          label={label}
        >
          {showDefault && defaultStrategy && (
            <MenuItem value="default">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography>Default Strategy</Typography>
                {renderStrategyChip(defaultStrategy)}
              </Box>
            </MenuItem>
          )}
          
          {strategies
            .filter(s => s.is_active)
            .map((strategy) => (
              <MenuItem key={strategy.id} value={strategy.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography>{strategy.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {renderStrategyChip(strategy)}
                    {allowCreate && (
                      <Tooltip title="Edit strategy">
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStrategy(strategy);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {showPreview && renderStrategyPreview(getSelectedStrategy())}

      {allowCreate && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleCreateStrategy}
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
          disabled={disabled}
        >
          Create New Strategy
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Create Strategy Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Retry Strategy</DialogTitle>
        <DialogContent>
          <RetryStrategyForm
            strategy={null}
            onSave={handleSaveStrategy}
            onCancel={() => setCreateDialogOpen(false)}
            mode="create"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Strategy Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Retry Strategy</DialogTitle>
        <DialogContent>
          <RetryStrategyForm
            strategy={editingStrategy}
            onSave={handleSaveStrategy}
            onCancel={() => setEditDialogOpen(false)}
            mode="edit"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RetryStrategySelector; 